import { useState } from 'react';
import { HeartPulse } from 'lucide-react';
import { acceptConsent } from './useConsent';
import { isSupabaseConfigured } from '@/lib/supabase';

/**
 * Consentimiento explícito para tratar datos de salud (art. 9 RGPD).
 *
 * No hay casilla premarcada y no se puede seguir sin aceptar: es la definición
 * de consentimiento válido. El texto es corto a propósito — una política que
 * nadie lee no informa a nadie.
 */
export function ConsentScreen() {
  const [busy, setBusy] = useState(false);

  async function accept() {
    setBusy(true);
    try {
      await acceptConsent();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col justify-center gap-5 px-6 py-8">
      <div className="flex flex-col items-center gap-2 text-center">
        <HeartPulse aria-hidden="true" className="size-10 text-[var(--color-brand-strong)]" />
        <h1 className="text-xl font-bold">Antes de empezar</h1>
      </div>

      <div className="flex flex-col gap-3 text-sm leading-relaxed">
        <p>
          NutriGym registra <strong>datos de salud</strong>: lo que comes, tu peso y tus
          entrenamientos. La ley los considera de categoría especial, así que necesitamos tu permiso
          explícito para tratarlos.
        </p>

        <ul className="flex list-disc flex-col gap-1.5 pl-5 text-[var(--color-text-muted)]">
          <li>Los datos son tuyos y solo tú los ves. Nadie más tiene acceso.</li>
          <li>
            {isSupabaseConfigured
              ? 'Se guardan en tu dispositivo y en un servidor alojado en la Unión Europea.'
              : 'Se guardan únicamente en este dispositivo.'}
          </li>
          <li>No hay publicidad, ni analítica de terceros, ni venta de datos.</li>
          <li>Puedes exportarlos o borrarlos por completo cuando quieras, desde Perfil.</li>
          <li>
            Las recomendaciones las calcula la propia app con reglas fijas, no una inteligencia
            artificial.
          </li>
        </ul>

        <p className="text-[var(--color-text-muted)]">
          NutriGym no sustituye el consejo de un médico ni de un dietista-nutricionista.
        </p>
      </div>

      <button
        type="button"
        onClick={() => void accept()}
        disabled={busy}
        className="tap rounded-xl bg-[var(--color-brand-button)] px-4 font-semibold text-[var(--color-on-brand)] disabled:opacity-60"
      >
        Acepto y quiero continuar
      </button>

      <p className="text-center text-xs text-[var(--color-text-muted)]">
        Si no aceptas, no puedes usar la app: sin tratar estos datos no hay nada que registrar.
      </p>
    </div>
  );
}
